import {
  validatePersonName,
  validateLocalPhoneForCountry,
  validateEmail,
} from './personFieldValidation';
import { phoneLengthForDialCode } from '../services/countryDialCodeService';

export type PartnerFormErrors = Record<string, string>;

export const PARTNER_FIELD_LABELS: Record<string, string> = {
  firstName: 'First name',
  lastName: 'Last name',
  email: 'Email',
  phone: 'Phone',
  password: 'Password',
  confirmPassword: 'Confirm password',
  zoneId: 'Zone',
  contact: 'Contact',
};

export interface PartnerContactFormInput {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  phoneCountryCode: string;
  password?: string;
  confirmPassword?: string;
  zoneId?: string;
}

export interface ValidatePartnerFormOptions {
  requiresZone?: boolean;
  zones?: Array<{ _id: string }>;
  requirePassword?: boolean;
}

export function validatePartnerContactForm(
  form: PartnerContactFormInput,
  opts: ValidatePartnerFormOptions = {},
): PartnerFormErrors {
  const next: PartnerFormErrors = {};
  const { requiresZone = false, zones = [], requirePassword = false } = opts;

  const first = validatePersonName(form.firstName, 'First name', { required: true });
  if (first) next.firstName = first;

  const last = validatePersonName(form.lastName, 'Last name');
  if (last) next.lastName = last;

  const emailTrim = form.email.trim();
  const phoneTrim = form.phone.trim();
  const emailRequired = !phoneTrim;
  const phoneRequired = !emailTrim;

  if (!emailTrim && !phoneTrim) {
    next.contact = 'Email or phone is required';
  }

  const emailErr = validateEmail(form.email, 'Email', { required: emailRequired });
  if (emailErr) next.email = emailErr;

  if (phoneTrim && !form.phoneCountryCode) {
    next.phone = 'Select a country code before entering the phone number';
  } else {
    const phoneErr = validateLocalPhoneForCountry(
      form.phone,
      phoneLengthForDialCode(form.phoneCountryCode),
      'Phone number',
      {
        required: phoneRequired,
        dialCode: form.phoneCountryCode,
      },
    );
    if (phoneErr) next.phone = phoneErr;
  }

  if (requirePassword) {
    const pwd = (form.password || '').trim();
    const confirm = (form.confirmPassword || '').trim();
    if (!pwd) {
      next.password = 'Password is required';
    } else if (pwd.length < 6) {
      next.password = 'Password must be at least 6 characters';
    }
    if (!confirm) {
      if (pwd) next.confirmPassword = 'Confirm password is required';
    } else if (pwd && pwd !== confirm) {
      next.confirmPassword = 'Passwords do not match';
    } else if (!pwd && confirm) {
      next.confirmPassword = 'Enter the same password in both fields';
    }
  }

  if (requiresZone) {
    const zoneId = (form.zoneId || '').trim();
    if (!zoneId) {
      next.zoneId = 'Operating zone is required for this partner type';
    } else if (!zones.some((z) => z._id === zoneId)) {
      next.zoneId = 'Select a valid zone from the list';
    }
  }

  return next;
}

/** Unique validation messages for summary display (dedupes contact + field errors). */
export function partnerValidationSummary(errors: PartnerFormErrors): string[] {
  const seen = new Set<string>();
  const lines: string[] = [];
  for (const [key, message] of Object.entries(errors)) {
    if (!message || seen.has(message)) continue;
    seen.add(message);
    const label = PARTNER_FIELD_LABELS[key];
    lines.push(label ? `${label}: ${message}` : message);
  }
  return lines;
}

export function clearPartnerFieldErrors(
  prev: PartnerFormErrors,
  keys: string[],
): PartnerFormErrors {
  if (!keys.length) return prev;
  const next = { ...prev };
  for (const key of keys) delete next[key];
  if (keys.some((k) => k === 'email' || k === 'phone')) delete next.contact;
  return next;
}
